import type { CountryCode } from "@/constants/country";
import { SupportedCountry } from "@/constants/country";
import type { TFunction } from "@/lib/i18n";
import type { FlagComponent } from "country-flag-icons/react/3x2";
import * as flags from "country-flag-icons/react/3x2";
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import { assign, emit, setup } from "xstate";
import { phoneNumberSchema } from "./zod-schema";

/**
 * 国情報を表す型
 */
export type Country = {
  /** ISO 3166-1 alpha-2 形式の国コード */
  code: CountryCode;
  /** 国際電話番号の国番号（例: "81"） */
  dialCode: string;
  /** 国旗の絵文字 */
  flag: FlagComponent;
};

/**
 * 電話番号の値を表す型
 */
export type PhoneValue = {
  /** 電話番号（ハイフンあり） */
  phoneNumber: string;
  /** 国際電話番号の国番号（例: "81"） */
  dialCode: string;
  /** ISO 3166-1 alpha-2 形式の国コード（任意） */
  countryCode?: CountryCode;
};

/**
 * 電話入力マシンのコンテキスト型
 */
interface PhoneContext {
  /** 入力された電話番号 */
  phoneNumber: string;
  /** フォーマット済みの電話番号 */
  formattedNumber: string;
  /** 選択された国 */
  selectedCountry: Country;
  /** 利用可能な国のリスト */
  countries: Country[];
  /** 国選択のオプション */
  countryOptions: Array<{
    key: CountryCode;
    label: string;
    value: string;
  }>;
  /** 国検索のテキスト */
  searchText: string;
  /** エラーメッセージ */
  error: string | null;
  /** ポップオーバーの開閉状態 */
  isOpen: boolean;
}

/**
 * 電話入力マシンのイベント型
 */
type PhoneEvent =
  | { type: "PHONE_NUMBER_CHANGE"; value: string }
  | { type: "COUNTRY_SEARCH"; query: string }
  | { type: "COUNTRY_SELECT"; dialCode: string }
  | { type: "TOGGLE_POPOVER" };

/**
 * 値変更イベント型
 */
type ValueChangeEvent = {
  type: "valueChange";
  value: PhoneValue;
};

/**
 * 利用可能な国のリストを取得する
 */
const getInitialCountries = () => {
  return getCountries().map((code) => ({
    code,
    dialCode: getCountryCallingCode(code),
    flag: flags[code],
  }));
};

/**
 * 電話番号のschema定義
 */
const phoneNumberSchemaByIsRequired = (
  countryCode: CountryCode,
  required: boolean,
) =>
  required
    ? phoneNumberSchema(countryCode).refine((val) => val !== "", {
        params: {
          i18n: { key: "custom.required" },
        },
      })
    : phoneNumberSchema(countryCode).optional();

/**
 * 電話番号入力のステートマシンを作成する
 */
export const createPhoneInputMachine = ({
  initialValue,
  defaultCountry = SupportedCountry.JP,
  required,
  t,
}: {
  initialValue: PhoneValue;
  defaultCountry?: CountryCode;
  required: boolean;
  t: TFunction;
}) => {
  // 国のリストを初期化
  const countries = getInitialCountries();
  const countryOptions = countries.map((country) => ({
    key: country.code,
    label: `${t(`common.country.${country.code}`)} (+${country.dialCode})`,
    value: country.dialCode,
  }));

  // 初期国を取得
  const getInitialCountry = (): Country => {
    if (initialValue.dialCode) {
      const foundCountry = countries.find(
        (c) => c.dialCode === initialValue.dialCode,
      );
      if (foundCountry) return foundCountry;
    }
    const defaultCountryData = countries.find((c) => c.code === defaultCountry);
    if (!defaultCountryData) {
      throw new Error(`Invalid default country code: ${defaultCountry}`);
    }
    return defaultCountryData;
  };

  const initialCountry = getInitialCountry();

  const machine = setup({
    types: {
      context: {} as PhoneContext,
      events: {} as PhoneEvent,
      emitted: {} as ValueChangeEvent,
    },
    actions: {
      // 電話番号の更新アクション
      updatePhoneNumber: assign(({ context, event }) => {
        if (event.type !== "PHONE_NUMBER_CHANGE") return {};

        // 電話番号のフォーマットと検証
        const formatter = new AsYouType(context.selectedCountry.code);
        // 電話番号を国別フォーマットに自動変換
        const formattedNumber = formatter.input(event.value);
        const schema = phoneNumberSchemaByIsRequired(
          context.selectedCountry.code,
          required,
        );
        const result = schema.safeParse(event.value);

        // 無効な電話番号の場合はエラーメッセージを設定
        const errorMessage = result.success
          ? null
          : result.error.issues[0]?.message || "Invalid phone number";

        return {
          phoneNumber: event.value,
          formattedNumber,
          error: errorMessage,
        };
      }),

      // 値の変更を通知
      // @see https://stately.ai/docs/event-emitter
      notifyPhoneNumberChange: emit(({ context }) => ({
        type: "valueChange" as const,
        value: {
          phoneNumber: context.formattedNumber,
          dialCode: context.selectedCountry.dialCode,
          countryCode: context.selectedCountry.code,
        },
      })),

      // 選択した国の更新アクション
      updateSelectedCountry: assign(({ context, event }) => {
        if (event.type !== "COUNTRY_SELECT") return {};

        // USの場合は特別処理、dialCode="1"の場合は必ずUSとして扱う
        // https://www.linkstaff.co.jp/m-doctor/form/phone.html
        if (event.dialCode === "1") {
          const usCountry = context.countries.find(
            (c) => c.code === SupportedCountry.US,
          );
          if (usCountry) {
            const formatter = new AsYouType(SupportedCountry.US);
            const formattedNumber = formatter.input(context.phoneNumber);
            const schema = phoneNumberSchemaByIsRequired(
              SupportedCountry.US,
              required,
            );
            const result = schema.safeParse(context.phoneNumber);

            return {
              selectedCountry: usCountry,
              formattedNumber,
              error: result.success ? null : result.error.issues[0].message,
              isOpen: false,
            };
          }
        }

        // 通常の処理
        const country = context.countries.find(
          (c) => c.dialCode === event.dialCode,
        );
        if (!country) return {};

        // 国が変更された場合、電話番号を再フォーマットして検証
        const formatter = new AsYouType(country.code);
        const formattedNumber = formatter.input(context.phoneNumber);
        const schema = phoneNumberSchemaByIsRequired(country.code, required);
        const result = schema.safeParse(context.phoneNumber);

        return {
          selectedCountry: country,
          formattedNumber,
          error: result.success ? null : result.error.issues[0].message,
          isOpen: false,
        };
      }),

      // 国変更後の値を通知
      notifyCountryChange: emit(({ context }) => ({
        type: "valueChange" as const,
        value: {
          phoneNumber: context.formattedNumber,
          dialCode: context.selectedCountry.dialCode,
          countryCode: context.selectedCountry.code,
        },
      })),

      // ポップオーバーの切り替えアクション
      togglePopover: assign(({ context }) => ({
        isOpen: !context.isOpen,
      })),

      // 検索テキストの更新アクション
      updateSearchText: assign(({ event }) => ({
        searchText: event.type === "COUNTRY_SEARCH" ? event.query : "",
      })),

      // バリデーションエラーの設定アクション
      setValidationError: assign(({ context }) => {
        const schema = phoneNumberSchemaByIsRequired(
          context.selectedCountry.code,
          required,
        );
        const result = schema.safeParse(context.phoneNumber);

        return {
          error: result.success ? null : result.error.issues[0].message,
        };
      }),
    },
    guards: {
      // 電話番号が有効かどうかを判定するガード
      isValidPhoneNumber: ({ context }) => {
        if (!context.phoneNumber) return true;
        const schema = phoneNumberSchema(context.selectedCountry.code);
        return schema.safeParse(context.phoneNumber).success;
      },
    },
  });

  const formatter = initialValue.countryCode
    ? new AsYouType(initialValue.countryCode as CountryCode)
    : null;

  const formattedNumber = formatter
    ? formatter.input(initialValue.phoneNumber)
    : initialValue.phoneNumber;

  return machine.createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QAcAWB7AdmAkp5ArgC4B0AlhADZgDEACgBIDyAcgKID6LAqgLIBCbAEocAwgwCCLAOJsA2gAYAuohTpYZImSyqQAD0QB2AIyGSxgCwLDADgsWAzMYBsxmzYA0IAJ6IHATjMbYwAmAFYwh2dDZ3sbAF94rzQsXHxicipaUSZuFgAVIQBNDgBlNgAZNlF8xRUkEGR1TW1MXQMEQwczY1MA+zcXYK9fBBDDCxIrZwcHcId3YMTkjGw8QlIKahp8pmlpKo46JmOANWE63SaNLR0GjonJmMDnBTDgsJDjEcRTM39IsYwv4LGFDAp-HNlo1VmkNpltjk8oUSuUJEJxJcGtcWndQA9HCQFE4Aq8bAsIoYfghglN-PSbDNDJE3hZjNCUmt0qQAG4AQ0oFD5WkwUBoWLUN1a7V+0SJYQsMS6XRsIVeYWpn3+zhshn8X1BDk+CSSMNS6wy-MFEGFZFF4uM9UluLa91lZlZSu6CzVb2pFhCISmOt1bnBIX8bw5sItvIFFHFyiuzVurvxiGc43lFkZhi+5OJDk1WfzNgUpgs-mCNn80fN3PImCtCYljRT0rdCEzHoVufzDkL-q+JBMi0sE2ZzmciVNmHQEDgVxj3OTUrx+kQAFpnNTt0SFAfD0fD4Y61z4VswKuXTKaQog8TIrY3rqZsXnCR84YJsErECz3ClrxjaIpQNeqa3gGmrGP4JBll8xJAlOoQAbGJDNhA4EdumCABmYThqsCCjqg4Fg7j4iBhPeI5uKq-ixIy+rTqanKAZsTbAVh64dAExgkJ8RrGG8rwHlWVIUQgARmPmkaQl0Mz3mEM7xEAA */
    id: "phoneInput",
    initial: "idle",
    context: {
      phoneNumber: initialValue.phoneNumber,
      formattedNumber: formattedNumber,
      selectedCountry: initialCountry,
      countries,
      countryOptions,
      searchText: "",
      error: null,
      isOpen: false,
    },
    states: {
      idle: {
        on: {
          PHONE_NUMBER_CHANGE: {
            actions: ["updatePhoneNumber", "notifyPhoneNumberChange"],
          },
          COUNTRY_SELECT: {
            actions: ["updateSelectedCountry", "notifyCountryChange"],
          },
          TOGGLE_POPOVER: {
            actions: "togglePopover",
          },
          COUNTRY_SEARCH: {
            actions: "updateSearchText",
          },
        },
      },
      validating: {
        always: [
          {
            guard: {
              type: "isValidPhoneNumber",
            },
            target: "valid",
          },
          { target: "invalid" },
        ],
      },
      valid: {
        entry: assign({ error: null }),
        always: { target: "idle" },
      },
      invalid: {
        entry: {
          type: "setValidationError",
        },
        always: { target: "idle" },
      },
    },
  });
};
